/**
 * Service Status Page
 * Admin page for monitoring infrastructure service health
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  LinearProgress,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SecurityIcon from '@mui/icons-material/Security';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useNotification } from '../../contexts/NotificationContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import StorageIcon from '@mui/icons-material/Storage';
import EmailIcon from '@mui/icons-material/Email';
import CloudIcon from '@mui/icons-material/Cloud';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ComputerIcon from '@mui/icons-material/Computer';
import CachedIcon from '@mui/icons-material/Cached';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { apiGet, apiPost } from '../../utils/apiClient';
import logger from '../../utils/logger';

const ServiceStatusPage = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const notify = useNotification();
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState({});
  const [overallStatus, setOverallStatus] = useState('unknown');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [testing, setTesting] = useState({});

  // Master tenant specific state
  const [tenantStatuses, setTenantStatuses] = useState([]);
  const [tenantSummary, setTenantSummary] = useState(null);
  const [pendingUsersCount, setPendingUsersCount] = useState(0);
  const [loadingTenants, setLoadingTenants] = useState(false);

  // Check if this is master tenant (server-authoritative flag from config/current)
  const isMasterTenant = tenant?.isMaster === true;

  const loadServiceStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiGet('/admin/service-status');
      setServices(response.services || {});
      setOverallStatus(response.overallStatus || 'unknown');
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (err) {
      notify.error('Failed to load service status: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- notify is stable
  }, []);

  // Load master tenant specific data
  const loadMasterTenantData = useCallback(async () => {
    if (!isMasterTenant) return;

    setLoadingTenants(true);
    try {
      // Load all tenant statuses and pending users count in parallel
      const [tenantsResponse, pendingResponse] = await Promise.all([
        apiGet('/admin/service-status/all-tenants'),
        apiGet('/admin/pending-users/count'),
      ]);

      if (tenantsResponse.success) {
        setTenantStatuses(tenantsResponse.tenants || []);
        setTenantSummary(tenantsResponse.summary || null);
      }

      if (pendingResponse.success) {
        setPendingUsersCount(pendingResponse.count || 0);
      }
    } catch (err) {
      logger.warn('Failed to load master tenant data', { message: err.message, status: err.response?.status });
    } finally {
      setLoadingTenants(false);
    }
  }, [isMasterTenant]);

  useEffect(() => {
    loadServiceStatus();
  }, [loadServiceStatus]);

  // Load master tenant data
  useEffect(() => {
    loadMasterTenantData();
  }, [loadMasterTenantData]);

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(loadServiceStatus, 30000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, loadServiceStatus]);

  const handleTestService = async (serviceName) => {
    try {
      setTesting(prev => ({ ...prev, [serviceName]: true }));
      await apiPost(`/admin/service-status/${serviceName}/test`);
      await loadServiceStatus();
    } catch (err) {
      notify.error(`Failed to test ${serviceName}: ${err.message}`);
    } finally {
      setTesting(prev => ({ ...prev, [serviceName]: false }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'unhealthy': return 'error';
      case 'degraded': return 'warning';
      case 'not_configured': return 'default';
      default: return 'default';
    }
  };

  const getServiceIcon = (serviceName) => {
    switch (serviceName) {
      case 'database': return <StorageIcon />;
      case 'email': return <EmailIcon />;
      case 'blob': return <CloudIcon />;
      case 'jobs': return <ScheduleIcon />;
      case 'server': return <ComputerIcon />;
      case 'cache': return <CachedIcon />;
      case 'exchangeRates': return <CurrencyExchangeIcon />;
      case 'keycloak': return <SecurityIcon />;
      default: return <StorageIcon />;
    }
  };

  const getServiceLabel = (serviceName) => {
    switch (serviceName) {
      case 'database': return 'PostgreSQL Database';
      case 'email': return 'Email Service (SMTP)';
      case 'blob': return 'Azure Blob Storage';
      case 'jobs': return 'Scheduled Jobs';
      case 'server': return 'Server Metrics';
      case 'cache': return 'Connection Cache';
      case 'exchangeRates': return 'Exchange Rates API';
      case 'keycloak': return 'Keycloak Identity Provider';
      default: return serviceName;
    }
  };

  const canTestService = (serviceName) => {
    return ['database', 'email', 'blob', 'keycloak'].includes(serviceName);
  };

  if (loading && Object.keys(services).length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Service Status
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor the health and connectivity of infrastructure services
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
            }
            label="Auto-refresh (30s)"
          />
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={loadServiceStatus}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      {/* Overall Status Banner - Compact */}
      <Paper
        sx={{
          py: 1,
          px: 2,
          mb: 2,
          bgcolor: overallStatus === 'healthy' ? 'success.main' :
                   overallStatus === 'unhealthy' ? 'error.main' :
                   overallStatus === 'degraded' ? 'warning.main' : 'grey.500',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Overall Status: {overallStatus.charAt(0).toUpperCase() + overallStatus.slice(1)}
          </Typography>
          {lastUpdated && (
            <Typography variant="body2">
              Last updated: {lastUpdated}
            </Typography>
          )}
        </Box>
      </Paper>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {/* Master Tenant Summary Cards */}
      {isMasterTenant && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/admin/pending-users')}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <PeopleIcon color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h4">{pendingUsersCount}</Typography>
                <Typography variant="body2" color="text.secondary">Pending Users</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <Card sx={{ cursor: 'pointer' }} onClick={() => navigate('/admin/tenants')}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <BusinessIcon color="info" sx={{ fontSize: 32 }} />
                <Typography variant="h4">{tenantSummary?.total || 0}</Typography>
                <Typography variant="body2" color="text.secondary">Active Tenants</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <ErrorIcon color={tenantSummary?.unhealthy > 0 ? 'error' : 'success'} sx={{ fontSize: 32 }} />
                <Typography variant="h4" color={tenantSummary?.unhealthy > 0 ? 'error.main' : 'success.main'}>
                  {tenantSummary?.unhealthy || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Unhealthy Services</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid
            size={{
              xs: 6,
              sm: 3
            }}>
            <Card
              sx={{ cursor: 'pointer' }}
              onClick={() => window.open(
                `${process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8080'}/admin/${process.env.REACT_APP_KEYCLOAK_REALM || 'capital-management-external'}/console/`,
                '_blank'
              )}
            >
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <SecurityIcon color="warning" sx={{ fontSize: 32 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <Typography variant="body1">Keycloak</Typography>
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </Box>
                <Typography variant="body2" color="text.secondary">Identity Admin</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
      {/* Master Tenant - Tenant Health Grid */}
      {isMasterTenant && tenantStatuses.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Tenant Database Health</Typography>
          {loadingTenants && <LinearProgress sx={{ mb: 1 }} />}
          <Grid container spacing={1}>
            {tenantStatuses.map((t) => (
              <Grid
                key={t.id}
                size={{
                  xs: 6,
                  sm: 4,
                  md: 3
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1, bgcolor: 'grey.50' }}>
                  {t.status === 'healthy' ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : t.status === 'not_provisioned' ? (
                    <WarningIcon color="warning" fontSize="small" />
                  ) : (
                    <ErrorIcon color="error" fontSize="small" />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>{t.name}</Typography>
                    {t.responseTimeMs && (
                      <Typography variant="caption" color="text.secondary">{t.responseTimeMs}ms</Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
      {/* Services Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600 }}>Service</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Info</TableCell>
              <TableCell sx={{ fontWeight: 600, width: 80 }} align="center">Test</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(services).map(([serviceName, service]) => (
              <ServiceRow
                key={serviceName}
                serviceName={serviceName}
                service={service}
                icon={getServiceIcon(serviceName)}
                label={getServiceLabel(serviceName)}
                statusColor={getStatusColor(service.status)}
                canTest={canTestService(serviceName)}
                isTesting={testing[serviceName]}
                onTest={() => handleTestService(serviceName)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

/**
 * Service Row Component - Compact table row with inline details
 */
const ServiceRow = ({
  serviceName,
  service,
  icon,
  label,
  statusColor,
  canTest,
  isTesting,
  onTest,
}) => {
  // Get inline info for each service
  const getInlineInfo = () => {
    if (service.error) {
      return <Typography variant="body2" color="error.main" sx={{ fontSize: '0.8rem' }}>{service.error}</Typography>;
    }

    const details = service.details;
    if (!details) return '-';

    switch (serviceName) {
      case 'database':
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{details.host}:{details.port}/{details.database}</Typography>
            {service.responseTimeMs && <Chip size="small" label={`${service.responseTimeMs}ms`} variant="outlined" />}
            {details.poolStats && (
              <>
                <Chip size="small" label={`Pool: ${details.poolStats.totalCount}`} variant="outlined" />
                <Chip size="small" label={`Idle: ${details.poolStats.idleCount}`} variant="outlined" color="success" />
              </>
            )}
          </Box>
        );
      case 'email':
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{details.host || 'N/A'}:{details.port}</Typography>
            <Chip size="small" label={details.configured ? 'Configured' : 'Not configured'} variant="outlined" color={details.configured ? 'success' : 'default'} />
            {service.responseTimeMs && <Chip size="small" label={`${service.responseTimeMs}ms`} variant="outlined" />}
          </Box>
        );
      case 'blob':
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Container: {details.containerName || 'N/A'}</Typography>
            <Chip size="small" label={details.configured ? 'Configured' : 'Not configured'} variant="outlined" color={details.configured ? 'success' : 'default'} />
          </Box>
        );
      case 'jobs':
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip size="small" label={details.running ? 'Running' : 'Stopped'} variant="outlined" color={details.running ? 'success' : 'error'} />
          </Box>
        );
      case 'server':
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{details.uptimeFormatted}</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{details.platform}</Typography>
            <Chip size="small" label={`Node ${details.nodeVersion}`} variant="outlined" />
            {details.memory && <Chip size="small" label={`Heap: ${Math.round(details.memory.heapUsed)}MB`} variant="outlined" />}
          </Box>
        );
      case 'cache':
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip size="small" label={`${details.tenantPools || 0} tenant pools`} variant="outlined" />
            {details.masterPool && (
              <Chip size="small" label={`Master: ${details.masterPool.totalCount} total, ${details.masterPool.idleCount} idle`} variant="outlined" />
            )}
          </Box>
        );
      case 'exchangeRates':
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip size="small" label={details.enabled ? 'Enabled' : 'Disabled'} variant="outlined" color={details.enabled ? 'success' : 'default'} />
            <Chip size="small" label={`${details.configuredPairs || 0} pairs`} variant="outlined" />
            {details.appIdConfigured && <Chip size="small" label="API Key OK" variant="outlined" color="success" />}
            {details.currentRates?.slice(0, 3).map((rate) => (
              <Chip key={rate.pair} size="small" variant="outlined" label={`${rate.pair}: ${rate.rate?.toFixed(4) || 'N/A'}`} />
            ))}
          </Box>
        );
      case 'keycloak':
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {details.url && <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{details.realm}@{details.url.replace(/^https?:\/\//, '')}</Typography>}
            {details.server && (
              <Chip size="small" label={details.server.status === 'healthy' ? 'Server OK' : 'Server Err'}
                    color={details.server.status === 'healthy' ? 'success' : 'error'} variant="outlined" />
            )}
            {details.adminApi && (
              <Chip size="small" label={details.adminApi.status === 'healthy' ? 'Admin OK' : 'Admin Err'}
                    color={details.adminApi.status === 'healthy' ? 'success' : 'error'} variant="outlined" />
            )}
            {details.jwks && (
              <Chip size="small" label={details.jwks.status === 'healthy' ? `JWKS OK (${details.jwks.keyCount} keys)` : 'JWKS Err'}
                    color={details.jwks.status === 'healthy' ? 'success' : 'error'} variant="outlined" />
            )}
            {!details.url && <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Not configured</Typography>}
          </Box>
        );
      default:
        return '-';
    }
  };

  return (
    <TableRow hover>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography variant="body2">{label}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Chip label={service.status} color={statusColor} size="small" sx={{ fontSize: '0.75rem' }} />
      </TableCell>
      <TableCell>
        {typeof getInlineInfo() === 'string' ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>{getInlineInfo()}</Typography>
        ) : getInlineInfo()}
      </TableCell>
      <TableCell align="center">
        {canTest && (
          <Tooltip title="Test Connection">
            <IconButton size="small" onClick={onTest} disabled={isTesting} color="primary">
              {isTesting ? <CircularProgress size={16} /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </TableCell>
    </TableRow>
  );
};

export default ServiceStatusPage;
